<template>
  <v-container>
    <h1 class="text-h4 mb-4">Manage Enterprise Quotes</h1>

    <v-snackbar
      v-model="showSuccessSnackbar"
      :timeout="5000"
      color="success"
      location="top"
    >
      {{ snackbarMessage }}
      <template v-slot:actions>
        <v-btn color="white" variant="text" @click="showSuccessSnackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
    <v-alert v-if="error" type="error" prominent class="my-4">{{ error }}</v-alert>

    <v-card>
      <v-card-text>
        <v-text-field
          v-model="searchQuery"
          label="Search by Company, Email, or ID"
          prepend-inner-icon="fas fa-search"
          variant="outlined"
          dense
          clearable
          class="mb-4"
        ></v-text-field>

        <v-tabs v-model="activeTab" bg-color="primary">
          <v-tab value="active">Active Quotes</v-tab>
          <v-tab value="archived">Archived Quotes</v-tab>
        </v-tabs>

        <v-window v-model="activeTab">
          <v-window-item value="active">
            <v-data-table
              :headers="headers"
              :items="filteredQuotes"
              :loading="loading"
              class="elevation-1 mt-4"
              :search="searchQuery"
            >
              <template #[`item.base_amount`]="{ item }">
                ${{ item.base_amount.toFixed(2) }}
              </template>
              <template #[`item.usage_unit_price`]="{ item }">
                ${{ item.usage_unit_price.toFixed(2) }}
              </template>
              <template #[`item.is_accepted`]="{ item }">
                <v-chip :color="item.is_accepted ? 'green' : 'red'" variant="flat">
                  {{ item.is_accepted ? 'Accepted' : 'Pending' }}
                </v-chip>
              </template>
              <template #[`item.actions`]="{ item }">
                <v-tooltip text="Edit Quote">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      v-bind="props"
                      icon="fas fa-edit"
                      size="small"
                      variant="text"
                      @click="openEditModal(item)"
                    ></v-btn>
                  </template>
                </v-tooltip>
                <v-tooltip :text="item.is_archived ? 'Unarchive Quote' : 'Archive Quote'">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      v-bind="props"
                      :icon="item.is_archived ? 'fas fa-box-open' : 'fas fa-archive'"
                      size="small"
                      variant="text"
                      @click="openArchiveConfirmModal(item)"
                    ></v-btn>
                  </template>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-window-item>

          <v-window-item value="archived">
            <v-data-table
              :headers="headers"
              :items="filteredQuotes"
              :loading="loading"
              class="elevation-1 mt-4"
              :search="searchQuery"
            >
              <template #[`item.base_amount`]="{ item }">
                ${{ item.base_amount.toFixed(2) }}
              </template>
              <template #[`item.usage_unit_price`]="{ item }">
                ${{ item.usage_unit_price.toFixed(2) }}
              </template>
              <template #[`item.is_accepted`]="{ item }">
                <v-chip :color="item.is_accepted ? 'green' : 'red'" variant="flat">
                  {{ item.is_accepted ? 'Accepted' : 'Pending' }}
                </v-chip>
              </template>
              <template #[`item.actions`]="{ item }">
                <v-tooltip text="Edit Quote">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      v-bind="props"
                      icon="fas fa-edit"
                      size="small"
                      variant="text"
                      @click="openEditModal(item)"
                    ></v-btn>
                  </template>
                </v-tooltip>
                <v-tooltip :text="item.is_archived ? 'Unarchive Quote' : 'Archive Quote'">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      v-bind="props"
                      :icon="item.is_archived ? 'fas fa-box-open' : 'fas fa-archive'"
                      size="small"
                      variant="text"
                      @click="openArchiveConfirmModal(item)"
                    ></v-btn>
                  </template>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-window-item>
        </v-window>
      </v-card-text>
    </v-card>

    <!-- Archive Confirmation Modal -->
    <v-dialog v-model="showArchiveConfirmModal" max-width="500px">
      <v-card>
        <v-card-title class="text-h5">Confirm Action</v-card-title>
        <v-card-text>
          Are you sure you want to {{ quoteToArchive?.is_archived ? 'unarchive' : 'archive' }} this quote?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue-darken-1" variant="text" @click="closeArchiveConfirmModal">
            Cancel
          </v-btn>
          <v-btn color="red-darken-1" variant="text" @click="archiveQuote">
            {{ quoteToArchive?.is_archived ? 'Unarchive' : 'Archive' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Quote Modal -->
    <v-dialog v-model="showEditModal" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Edit Quote #{{ editingQuote?.id }}</span>
        </v-card-title>
        <v-card-text>
          <v-container v-if="editingQuote">
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model.number="editingQuote.base_amount"
                  label="Monthly Subscription Amount"
                  type="number"
                  prefix="$"
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model.number="editingQuote.usage_unit_price"
                  label="TrueDepth Usage Base Cost"
                  type="number"
                  prefix="$"
                  required
                ></v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue-darken-1" variant="text" @click="closeEditModal">
            Cancel
          </v-btn>
          <v-btn color="blue-darken-1" variant="text" @click="patchQuote">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-vue';

const { getAccessTokenSilently } = useAuth0();

const allQuotes = ref([]);
const loading = ref(true);
const error = ref(null);
const showEditModal = ref(false);
const editingQuote = ref(null);
const showArchiveConfirmModal = ref(false);
const quoteToArchive = ref(null);
const searchQuery = ref('');
const snackbarMessage = ref('');
const showSuccessSnackbar = ref(false);

const activeTab = ref('active');

const headers = [
  { title: 'ID', key: 'id' },
  { title: 'Company', key: 'organization' },
  { title: 'Email', key: 'email' },
  { title: 'Base Amount', key: 'base_amount' },
  { title: 'Usage Amount', key: 'usage_unit_price' },
  { title: 'Accepted', key: 'is_accepted' },
  { title: 'Created On', key: 'created_on' },
  { title: 'Actions', key: 'actions', sortable: false },
];

const filteredQuotes = computed(() => {
  let quotes = [];
  if (activeTab.value === 'active') {
    quotes = allQuotes.value.filter(quote => !quote.is_archived);
  } else if (activeTab.value === 'archived') {
    quotes = allQuotes.value.filter(quote => quote.is_archived);
  }

  if (!searchQuery.value) {
    return quotes;
  }

  const lowerCaseQuery = searchQuery.value.toLowerCase();
  return quotes.filter(quote => {
    return (
      quote.organization?.toLowerCase().includes(lowerCaseQuery) ||
      quote.email?.toLowerCase().includes(lowerCaseQuery) ||
      quote.id.toString().includes(lowerCaseQuery)
    );
  });
});

const fetchQuotes = async () => {
  loading.value = true;
  error.value = null;
  try {
    const token = await getAccessTokenSilently();
    const headers = { Authorization: `Bearer ${token}` };
    const response = await axios.get(
      `${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/quotes`,
      { headers }
    );
    allQuotes.value = response.data.quotes || [];
  } catch (e) {
    console.error('Error fetching quotes:', e);
    error.value = 'Failed to load quotes.';
  } finally {
    loading.value = false;
  }
};

const openEditModal = (quote) => {
  editingQuote.value = JSON.parse(JSON.stringify(quote));
  showEditModal.value = true;
};

const closeEditModal = () => {
  showEditModal.value = false;
  editingQuote.value = null;
};

const patchQuote = async () => {
  error.value = null;
  if (!editingQuote.value || !editingQuote.value.id) {
    error.value = 'Attempted to save an invalid quote. Please try again.';
    closeEditModal();
    return;
  }

  const quoteToEdit = editingQuote.value;

  try {
    const token = await getAccessTokenSilently();
    const headers = { Authorization: `Bearer ${token}` };

    const payload = {
      base_amount: quoteToEdit.base_amount,
      usage_unit_price: quoteToEdit.usage_unit_price,
    };

    await axios.patch(
      `${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/quotes/${quoteToEdit.id}`,
      payload,
      { headers }
    );

    await axios.post(
      `${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/quotes/${quoteToEdit.id}/resend`,
      {},
      { headers }
    );
    snackbarMessage.value = 'Quote saved and email sent successfully.';
    
    showSuccessSnackbar.value = true;

    await fetchQuotes();
    closeEditModal();
  } catch (e) {
    console.error('Error patching quote:', e);
    error.value = e.response?.data?.detail || 'Failed to update quote.';
  }
};

const openArchiveConfirmModal = (quote) => {
  quoteToArchive.value = quote;
  showArchiveConfirmModal.value = true;
};

const closeArchiveConfirmModal = () => {
  showArchiveConfirmModal.value = false;
  quoteToArchive.value = null;
};

const archiveQuote = async () => {
  if (!quoteToArchive.value) return;

  const quote = quoteToArchive.value;
  try {
    const token = await getAccessTokenSilently();
    const headers = { Authorization: `Bearer ${token}` };
    const payload = {
      is_archived: !quote.is_archived,
    };

    await axios.patch(
      `${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/quotes/${quote.id}`,
      payload,
      { headers }
    );
    await fetchQuotes();
    closeArchiveConfirmModal();
  } catch (e) {
    console.error('Error archiving quote:', e);
    error.value = e.response?.data?.detail || 'Failed to archive quote.';
  }
};

onMounted(fetchQuotes);
</script>

<style scoped>
/* Scoped styles for AdminQuotesView */
</style>